import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { getFileUrl } from '../../utils/fileUtils';
import AdminNavbar from '../../components/admin/AdminNavbar';
import HtmlEditor from '../../components/admin/HtmlEditor';
import './AdminCommon.css';

// Convierte URL de YouTube a URL de embed para iframe
function youtubeEmbedUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const u = url.trim();
  if (!u) return null;
  const match = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
}

const CLAVES = [
  { clave: 'descripcion_obispos', label: 'Descripción Obispos' },
  { clave: 'mision', label: 'Misión' },
  { clave: 'vision', label: 'Visión' },
  { clave: 'nuestro_obispo', label: 'Nuestro Obispo' },
  { clave: 'insignias', label: 'Insignias' }
];

const AdminSeccionesInicio = () => {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [titulo, setTitulo] = useState('');
  const [contenido, setContenido] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [imagenFile, setImagenFile] = useState(null);
  const [resumen, setResumen] = useState('');
  const [historiaImagenes, setHistoriaImagenes] = useState([]);
  const [replaceHistoriaImages, setReplaceHistoriaImages] = useState(false);
  const [removeHistoriaImages, setRemoveHistoriaImages] = useState(false);

  useEffect(() => {
    if (searchParams.get('seccion') === 'historia') setEditing('historia_pagina');
  }, [searchParams]);

  const { data: secciones = {}, isLoading } = useQuery({
    queryKey: ['secciones-inicio'],
    queryFn: async () => {
      const res = await api.get('/secciones-inicio');
      return res.data;
    }
  });

  const { data: historiaData, isLoading: historiaLoading } = useQuery({
    queryKey: ['secciones-paginas-admin', 'historia'],
    queryFn: async () => {
      const res = await api.get('/secciones-paginas/historia');
      return res.data;
    },
    retry: false,
  });

  const historiaImagenesActuales = useMemo(
    () => (Array.isArray(historiaData?.imagenes) ? historiaData.imagenes : []),
    [historiaData]
  );

  useEffect(() => {
    if (editing === 'historia_pagina' && historiaData) {
      setTitulo(historiaData.titulo || '');
      setResumen(historiaData.resumen || '');
      setContenido(historiaData.contenido || '');
      setVideoUrl(historiaData.video_url || '');
      setHistoriaImagenes([]);
      setReplaceHistoriaImages(false);
      setRemoveHistoriaImages(false);
    }
  }, [editing, historiaData]);

  const historiaMutation = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append('titulo', titulo);
      fd.append('resumen', resumen);
      fd.append('contenido', contenido);
      fd.append('video_url', videoUrl.trim() || '');
      fd.append('replace_images', String(replaceHistoriaImages));
      fd.append('remove_images', String(removeHistoriaImages));
      historiaImagenes.forEach((f) => fd.append('imagenes', f));
      const res = await api.put('/secciones-paginas/historia', fd);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['secciones-paginas-admin', 'historia']);
      setEditing(null);
      setHistoriaImagenes([]);
      setReplaceHistoriaImages(false);
      setRemoveHistoriaImages(false);
      alert('Historia actualizada correctamente.');
    },
    onError: (e) => alert('Error: ' + (e.response?.data?.error || e.message)),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ clave, titulo, contenido, video_url, imagen }) => {
      const formData = new FormData();
      if (titulo !== undefined) formData.append('titulo', titulo);
      if (contenido !== undefined) formData.append('contenido', contenido);
      if (video_url !== undefined) formData.append('video_url', video_url);
      if (imagen) formData.append('imagen', imagen);
      const res = await api.put(`/secciones-inicio/${clave}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['secciones-inicio']);
      setEditing(null);
      setTitulo('');
      setContenido('');
      setVideoUrl('');
      setImagenFile(null);
      alert('Sección actualizada correctamente.');
    },
    onError: (e) => {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  });

  const openEdit = (clave) => {
    if (clave === 'historia_pagina') {
      setEditing('historia_pagina');
      return;
    }
    const s = secciones[clave];
    setEditing(clave);
    setTitulo(s?.titulo || '');
    setContenido(s?.contenido || '');
    setVideoUrl(s?.video_url || '');
    setImagenFile(null);
  };

  const handleSave = (clave) => {
    if (clave === 'historia_pagina') {
      historiaMutation.mutate();
      return;
    }
    updateMutation.mutate({
      clave,
      titulo,
      contenido,
      video_url: videoUrl.trim() || '',
      imagen: imagenFile || undefined
    });
  };

  if (isLoading) return <div className="admin-common"><AdminNavbar /><div className="container">Cargando...</div></div>;

  return (
    <div className="admin-common">
      <AdminNavbar />
      <div className="container">
        <div className="admin-header">
          <Link to="/admin" className="btn btn-secondary">← Panel</Link>
          <h1>Secciones del Inicio / Acerca</h1>
          <p>Editar Historia (página completa), Misión, Visión, Obispo, Insignias. Formato HTML, múltiples imágenes (slider), video YouTube.</p>
        </div>

        <div className="secciones-inicio-admin">
          {/* Historia - página completa (secciones-paginas) - integrada en Secciones Inicio */}
          <div className="seccion-inicio-card seccion-pastoral-card">
            <h2>Historia (página completa)</h2>
            {editing === 'historia_pagina' ? (
              historiaLoading ? (
                <p>Cargando...</p>
              ) : (
                <div className="seccion-edit-form">
                  <label>Título *</label>
                  <input type="text" value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Título" />
                  <label>Resumen</label>
                  <textarea value={resumen} onChange={e => setResumen(e.target.value)} rows={3} placeholder="Resumen breve..." />
                  <HtmlEditor label="Contenido (HTML)" value={contenido} onChange={setContenido} rows={10} />
                  <label>URL de video (YouTube)</label>
                  <input type="url" value={videoUrl} onChange={e => setVideoUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=..." />
                  <label>Imágenes (varias = slider)</label>
                  {historiaImagenesActuales.length > 0 && (
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                      {historiaImagenesActuales.map((img, idx) => (
                        <img key={idx} src={getFileUrl(typeof img === 'string' ? img : (img?.imagen_url || img))} alt="" style={{ width: 100, height: 75, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd' }} />
                      ))}
                    </div>
                  )}
                  <input type="file" accept="image/*" multiple onChange={e => {
                    const arr = Array.from(e.target.files || []);
                    setHistoriaImagenes(arr);
                    if (arr.length) setReplaceHistoriaImages(true);
                  }} />
                  <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input type="checkbox" checked={replaceHistoriaImages} onChange={e => setReplaceHistoriaImages(e.target.checked)} disabled={historiaImagenes.length === 0} />
                      Reemplazar imágenes actuales
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input type="checkbox" checked={removeHistoriaImages} onChange={e => {
                        const v = e.target.checked;
                        setRemoveHistoriaImages(v);
                        if (v) { setHistoriaImagenes([]); setReplaceHistoriaImages(false); }
                      }} />
                      Eliminar todas las imágenes
                    </label>
                  </div>
                  <div className="form-actions">
                    <button type="button" className="btn btn-primary" onClick={() => handleSave('historia_pagina')} disabled={historiaMutation.isLoading}>Guardar</button>
                    <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)}>Cancelar</button>
                  </div>
                </div>
              )
            ) : (
              <>
                {historiaImagenesActuales.length > 0 && (
                  <div className="seccion-preview-img">
                    {historiaImagenesActuales.slice(0, 2).map((img, idx) => (
                      <img key={idx} src={getFileUrl(typeof img === 'string' ? img : (img?.imagen_url || img))} alt="" />
                    ))}
                    {historiaImagenesActuales.length > 2 && <span>+{historiaImagenesActuales.length - 2} más</span>}
                  </div>
                )}
                {historiaData?.video_url && youtubeEmbedUrl(historiaData.video_url) && (
                  <div className="seccion-preview-video">
                    <iframe title="Video Historia" src={youtubeEmbedUrl(historiaData.video_url)} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  </div>
                )}
                <p className="seccion-preview-text">
                  {(historiaData?.resumen || historiaData?.contenido || '').replace(/<[^>]+>/g, ' ').trim().substring(0, 150)}...
                </p>
                <button type="button" className="btn btn-primary" onClick={() => openEdit('historia_pagina')}>Editar</button>
              </>
            )}
          </div>

          {CLAVES.map(({ clave, label }) => {
            const s = secciones[clave];
            const embedUrl = youtubeEmbedUrl(s?.video_url);
            return (
              <div key={clave} className="seccion-inicio-card seccion-pastoral-card">
                <h2>{label}</h2>
                {editing === clave ? (
                  <div className="seccion-edit-form">
                    <label>Título</label>
                    <input
                      type="text"
                      value={titulo}
                      onChange={e => setTitulo(e.target.value)}
                      placeholder="Título de la sección"
                    />
                    <HtmlEditor
                      label="Contenido (HTML)"
                      value={contenido}
                      onChange={setContenido}
                      rows={8}
                      placeholder="Escribe aquí. Usa los botones para negrita, cursiva, listas y enlaces."
                    />
                    <label>URL de video (YouTube)</label>
                    <input
                      type="url"
                      value={videoUrl}
                      onChange={e => setVideoUrl(e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                    <small className="form-hint">Pega el enlace del video de YouTube; se mostrará incrustado en la sección.</small>
                    <label>Imagen (opcional, reemplaza la actual)</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={e => setImagenFile(e.target.files?.[0] || null)}
                    />
                    <div className="form-actions">
                      <button type="button" className="btn btn-primary" onClick={() => handleSave(clave)} disabled={updateMutation.isLoading}>
                        Guardar
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)}>Cancelar</button>
                    </div>
                  </div>
                ) : (
                  <>
                    {s?.imagen_url && (
                      <div className="seccion-preview-img">
                        <img src={getFileUrl(s.imagen_url)} alt={label} />
                      </div>
                    )}
                    {embedUrl && (
                      <div className="seccion-preview-video">
                        <iframe
                          title={`Video ${label}`}
                          src={embedUrl}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    )}
                    <p className="seccion-preview-text">
                      {(s?.contenido || '').replace(/<[^>]+>/g, ' ').trim().substring(0, 150)}
                      {(s?.contenido || '').length > 150 ? '...' : ''}
                    </p>
                    <button type="button" className="btn btn-primary" onClick={() => openEdit(clave)}>Editar</button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminSeccionesInicio;
